import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { formatDateOnly } from "@/lib/date-only";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  AlertOctagon,
  Plus,
  ArrowRight,
} from "lucide-react";

const incidentsSearchSchema = z.object({
  status: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/incidents")({
  validateSearch: incidentsSearchSchema,
  component: IncidentsPage,
});

interface IncidentSummary {
  id: string;
  incidentType: string;
  severity: string;
  date: string;
  location: string | null;
  description: string;
  status: string;
  reportedByName: string | null;
  createdAt: string;
}

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "reported", label: "Reported" },
  { value: "under_investigation", label: "Investigating" },
  { value: "closed", label: "Closed" },
] as const;

const SEVERITY_COLORS: Record<string, string> = {
  minor: "bg-blue-100 text-blue-800",
  moderate: "bg-yellow-100 text-yellow-800",
  serious: "bg-orange-100 text-orange-800",
  critical: "bg-red-100 text-red-800",
};

const TYPE_LABELS: Record<string, string> = {
  injury: "Injury",
  near_miss: "Near Miss",
  property_damage: "Property Damage",
  environmental: "Environmental",
  vehicle: "Vehicle",
  other: "Other",
};

function IncidentsPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const statusFilter = search.status ?? "all";
  const setStatusFilter = (value: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        status: value === "all" ? undefined : value,
      }),
      replace: true,
    });
  };

  const { data, isLoading } = useQuery({
    queryKey: ["incidents", statusFilter],
    queryFn: () =>
      apiGet<{ data: IncidentSummary[]; total: number }>(
        `/api/incidents?status=${statusFilter}&limit=50`
      ),
  });

  const incidents = data?.data || [];

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center justify-between pt-2">
        <h1 className="text-2xl font-bold text-text-primary">Incidents</h1>
        <Link to="/incidents/report">
          <Button size="sm" className="bg-paris-red hover:bg-paris-red-dark gap-1">
            <Plus className="h-4 w-4" />
            Report
          </Button>
        </Link>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.value}
            type="button"
            onClick={() => setStatusFilter(filter.value)}
            className={cn(
              "inline-flex items-center whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors shrink-0",
              statusFilter === filter.value
                ? "bg-paris-red text-white"
                : "bg-card text-text-secondary border border-border hover:bg-muted/50 hover:text-text-primary"
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      )}

      {!isLoading && incidents.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <AlertOctagon className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-lg font-medium text-muted-foreground">
              No incidents reported
            </p>
            <p className="text-sm text-muted-foreground/70 mt-1">
              Incidents will appear here when reported.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && incidents.length > 0 && (
        <div className="space-y-2">
          {incidents.map((incident) => (
            <Link
              key={incident.id}
              to="/incidents/$incidentId"
              params={{ incidentId: incident.id }}
            >
              <Card className="hover:bg-muted/30 transition-colors cursor-pointer">
                <CardContent className="py-3 px-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {incident.description.slice(0, 80)}
                        {incident.description.length > 80 ? "..." : ""}
                      </p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge
                          className={cn(
                            "text-xs px-1.5 py-0",
                            SEVERITY_COLORS[incident.severity]
                          )}
                        >
                          {incident.severity}
                        </Badge>
                        <Badge variant="outline" className="text-xs px-1.5 py-0">
                          {TYPE_LABELS[incident.incidentType] || incident.incidentType}
                        </Badge>
                        <Badge variant="secondary" className="text-xs px-1.5 py-0">
                          {incident.status.replace("_", " ")}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDateOnly(incident.date)}
                        {incident.location && ` - ${incident.location}`}
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
